import {Badge} from '@dropbox/dig-components/badge';
import {IconButton} from '@dropbox/dig-components/buttons';
import {Toggle} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, useTheme} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  CheckmarkLine,
  ChevronRightLine,
  CopyLine,
  HelpLine,
  OpenLine,
} from '@dropbox/dig-icons/assets';
import {pulseUserAtom} from 'atoms/auth';
import {
  growthbookExperimentDefinitions,
  growthbookExperimentOverridesForCurrentUserAtom,
  growthbookFeatureDefinitions,
  growthbookOverridesForCurrentUserAtom,
  type GrowthbookOverrideValue,
  growthbookServerExperimentsAtom,
  growthbookServerFlagsAtom,
  setGrowthbookExperimentOverrideForCurrentUserAtom,
  setGrowthbookOverrideForCurrentUserAtom,
} from 'atoms/layout';
import {themePreferenceAtom} from 'atoms/settings';
import {ROUTE_PATHS} from 'constant';
import {buildGrowthbookTempOverrideUrl, getGrowthbookAppFeatureUrl} from 'helpers/growthbook';
import {isSuperUser} from 'helpers/utils';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {activateCookiesModule} from 'lib/initializePrivacyModule';
import {type MouseEvent} from 'react';
import {useNavigate} from 'react-router';

export const DROPBOX_PRIVACY_URL = 'https://www.dropbox.com/privacy';

/**
 * Help / legal / cookies and (for superusers) theme + GrowthBook overrides.
 * Render inside a menu content area, e.g. planning doc nav or the account menu.
 */
export const AppHelpMenuSegments = () => {
  return (
    <>
      <Menu.Segment>
        <AppHelpMenuActions />
      </Menu.Segment>
      <AppSuperUserMenuSegment />
    </>
  );
};

export const AppHelpMenuActions = () => {
  const navigate = useNavigate();

  const onManageCookiesClick = () => {
    activateCookiesModule();
  };

  return (
    <>
      <Menu.ActionItem onClick={() => navigate(ROUTE_PATHS.FEEDBACK)}>
        {t('feedback')}
      </Menu.ActionItem>
      <Menu.ActionItem onClick={() => window.open(DROPBOX_PRIVACY_URL, '_blank')}>
        {t('privacy_legal')}
      </Menu.ActionItem>
      <Menu.ActionItem onClick={() => onManageCookiesClick()}>
        {t('manage_cookies')}
      </Menu.ActionItem>
    </>
  );
};

export const AppSuperUserMenuSegment = () => {
  const {mode} = useTheme();
  const pulseUser = useAtomValue(pulseUserAtom);
  const growthbookServerFlags = useAtomValue(growthbookServerFlagsAtom);
  const growthbookServerExperiments = useAtomValue(growthbookServerExperimentsAtom);
  const growthbookOverrides = useAtomValue(growthbookOverridesForCurrentUserAtom);
  const growthbookExperimentOverrides = useAtomValue(
    growthbookExperimentOverridesForCurrentUserAtom
  );
  const setThemePreference = useSetAtom(themePreferenceAtom);
  const setGrowthbookOverride = useSetAtom(setGrowthbookOverrideForCurrentUserAtom);
  const setGrowthbookExperimentOverride = useSetAtom(
    setGrowthbookExperimentOverrideForCurrentUserAtom
  );
  const growthbookOverrideCount =
    Object.keys(growthbookOverrides).length + Object.keys(growthbookExperimentOverrides).length;

  const copyGrowthbookTempOverrideLink = async (
    growthbookFeatureId: string,
    serverEnabled: boolean
  ) => {
    await navigator.clipboard.writeText(
      buildGrowthbookTempOverrideUrl(growthbookFeatureId, {
        value: serverEnabled ? 'OFF' : 'ON',
      })
    );
  };

  if (!pulseUser || !isSuperUser(pulseUser)) {
    return null;
  }

  return (
    <Menu.Segment className="meticulous-ignore">
      <Menu.ActionItem
        onClick={() => setThemePreference(mode === 'dark' ? 'bright' : 'dark')}
        withRightAccessory={
          <Toggle isToggled={mode === 'dark'} aria-label={t('settings_theme_dark')} />
        }
      >
        Dark mode
      </Menu.ActionItem>
      <Menu.Submenu
        withTriggerContent={<GrowthbookOverridesMenuTrigger count={growthbookOverrideCount} />}
        withRightAccessory={
          <UIIcon src={ChevronRightLine} className={atoms({color: 'Text Subtle'})} />
        }
        closeOnSelection={false}
      >
        <Menu.Segment className="meticulous-ignore" withLabel="This browser only">
          {growthbookFeatureDefinitions.map(({key, label, growthbookFeatureId}) => (
            <Menu.Submenu
              key={key}
              withTriggerContent={
                <GrowthbookOverrideTrigger
                  label={label}
                  overrideValue={growthbookOverrides[key]}
                  serverEnabled={growthbookServerFlags[key]}
                />
              }
              withRightAccessory={
                <UIIcon src={ChevronRightLine} className={atoms({color: 'Text Subtle'})} />
              }
              closeOnSelection={false}
            >
              <Menu.Segment className="meticulous-ignore" withLabel={label}>
                <Menu.ActionItem
                  onClick={() => setGrowthbookOverride({featureKey: key, value: 'default'})}
                  withRightAccessory={
                    growthbookOverrides[key] === undefined ? (
                      <UIIcon src={CheckmarkLine} />
                    ) : undefined
                  }
                >
                  Default ({growthbookServerFlags[key] ? 'currently on' : 'currently off'})
                </Menu.ActionItem>
                <Menu.ActionItem
                  onClick={() => setGrowthbookOverride({featureKey: key, value: 'on'})}
                  withRightAccessory={
                    growthbookOverrides[key] === 'on' ? <UIIcon src={CheckmarkLine} /> : undefined
                  }
                >
                  Forced on
                </Menu.ActionItem>
                <Menu.ActionItem
                  onClick={() => setGrowthbookOverride({featureKey: key, value: 'off'})}
                  withRightAccessory={
                    growthbookOverrides[key] === 'off' ? <UIIcon src={CheckmarkLine} /> : undefined
                  }
                >
                  Forced off
                </Menu.ActionItem>
                {growthbookFeatureId ? (
                  <Menu.ActionItem
                    withRightAccessory={<UIIcon src={CopyLine} />}
                    onClick={() =>
                      void copyGrowthbookTempOverrideLink(
                        growthbookFeatureId,
                        growthbookServerFlags[key]
                      )
                    }
                  >
                    Copy temp override link
                  </Menu.ActionItem>
                ) : null}
                {growthbookFeatureId ? (
                  <Menu.ActionItem
                    withRightAccessory={<UIIcon src={OpenLine} />}
                    onClick={() =>
                      window.open(
                        getGrowthbookAppFeatureUrl(growthbookFeatureId),
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  >
                    {t('growthbook_open_in_growthbook')}
                  </Menu.ActionItem>
                ) : null}
              </Menu.Segment>
            </Menu.Submenu>
          ))}
          {growthbookExperimentDefinitions.map(({key, label, growthbookFeatureId, variants}) => (
            <Menu.Submenu
              key={key}
              withTriggerContent={
                <GrowthbookExperimentOverrideTrigger
                  label={label}
                  overrideValue={growthbookExperimentOverrides[key]}
                  serverValue={growthbookServerExperiments[key]}
                />
              }
              withRightAccessory={
                <UIIcon src={ChevronRightLine} className={atoms({color: 'Text Subtle'})} />
              }
              closeOnSelection={false}
            >
              <Menu.Segment className="meticulous-ignore" withLabel={label}>
                <Menu.ActionItem
                  onClick={() =>
                    setGrowthbookExperimentOverride({experimentKey: key, value: 'default'})
                  }
                  withRightAccessory={
                    growthbookExperimentOverrides[key] === undefined ? (
                      <UIIcon src={CheckmarkLine} />
                    ) : undefined
                  }
                >
                  Default (currently {growthbookServerExperiments[key]})
                </Menu.ActionItem>
                {variants.map((variant) => (
                  <Menu.ActionItem
                    key={variant}
                    onClick={() =>
                      setGrowthbookExperimentOverride({experimentKey: key, value: variant})
                    }
                    withRightAccessory={
                      growthbookExperimentOverrides[key] === variant ? (
                        <UIIcon src={CheckmarkLine} />
                      ) : undefined
                    }
                  >
                    {variant}
                  </Menu.ActionItem>
                ))}
                <Menu.ActionItem
                  withRightAccessory={<UIIcon src={OpenLine} />}
                  onClick={() =>
                    window.open(
                      getGrowthbookAppFeatureUrl(growthbookFeatureId),
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  {t('growthbook_open_in_growthbook')}
                </Menu.ActionItem>
              </Menu.Segment>
            </Menu.Submenu>
          ))}
        </Menu.Segment>
      </Menu.Submenu>
    </Menu.Segment>
  );
};

/** Same help menu as the main navbar, as a standalone `Menu.Wrapper` + trigger (planning doc top nav). */
export const AppHelpMenuDropdown = () => {
  return (
    <Menu.Wrapper shouldPropagateClickOutsideMouseEvents slideSubmenu>
      {({getContentProps, getTriggerProps}) => (
        <>
          <IconButton
            {...getTriggerProps()}
            variant="transparent"
            onMouseDown={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
            }}
            aria-label="icon button"
          >
            <UIIcon src={HelpLine} className={atoms({color: 'Text Subtle'})} />
          </IconButton>
          <Menu.Content
            {...getContentProps()}
            minWidth="200px"
            placement="bottom-end"
            positioningStrategy="fixed"
          >
            <AppHelpMenuSegments />
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

const getGrowthbookOverrideStatusLabel = (
  overrideValue: GrowthbookOverrideValue | undefined,
  serverEnabled: boolean
) => {
  if (overrideValue === 'on') {
    return 'Forced on';
  }
  if (overrideValue === 'off') {
    return 'Forced off';
  }
  return serverEnabled ? 'Default (on)' : 'Default (off)';
};

const GrowthbookOverridesMenuTrigger = ({count}: {count: number}) => (
  <Box
    display="flex"
    alignItems="center"
    style={{width: '100%', minWidth: 0, minHeight: 24, gap: 8}}
  >
    <Box display="flex" alignItems="center" style={{flex: 1, minWidth: 0}}>
      <Text>GrowthBook overrides</Text>
    </Box>
    {count > 0 ? (
      <Box data-testid="growthbook-overrides-count" display="flex" alignItems="center">
        <Badge
          style={{
            backgroundColor: 'var(--dig-color__text__base)',
            color: 'var(--dig-color__background__base)',
          }}
        >
          {count}
        </Badge>
      </Box>
    ) : null}
  </Box>
);

const GrowthbookOverrideTrigger = ({
  label,
  overrideValue,
  serverEnabled,
}: {
  label: string;
  overrideValue: GrowthbookOverrideValue | undefined;
  serverEnabled: boolean;
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    style={{width: '100%', minWidth: 0, minHeight: 24, gap: 12}}
  >
    <Box style={{flex: 1, minWidth: 0}}>
      <Text>{label}</Text>
    </Box>
    <Box display="flex" alignItems="center" style={{flexShrink: 0}}>
      <Text size="small" color="subtle">
        {getGrowthbookOverrideStatusLabel(overrideValue, serverEnabled)}
      </Text>
    </Box>
  </Box>
);

const GrowthbookExperimentOverrideTrigger = ({
  label,
  overrideValue,
  serverValue,
}: {
  label: string;
  overrideValue: string | undefined;
  serverValue: string;
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    style={{width: '100%', minWidth: 0, minHeight: 24, gap: 12}}
  >
    <Box style={{flex: 1, minWidth: 0}}>
      <Text>{label}</Text>
    </Box>
    <Box display="flex" alignItems="center" style={{flexShrink: 0}}>
      <Text size="small" color="subtle">
        {overrideValue && overrideValue !== 'default'
          ? `Forced: ${overrideValue}`
          : `Default (${serverValue})`}
      </Text>
    </Box>
  </Box>
);
